import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel } from "@/components/common";
import { ListEditor } from "@/components/settings/ListEditor";
import { JobTypeEditor } from "@/components/settings/JobTypeEditor";
import { StaffUsers } from "@/components/settings/StaffUsers";
import { CompaniesEditor } from "@/components/settings/CompaniesEditor";
import { PayRatesEditor } from "@/components/settings/PayRatesEditor";
import { FleetEditor } from "@/components/settings/FleetEditor";
import { BackupPanel } from "@/components/settings/BackupPanel";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const LISTS = [
  ["sales_people", "Sales people / booked by", "Used for commission filtering on the Bookings page"],
  ["teams", "Our drivers / teams", "Your own drivers – set each one's hourly wage under Pay & commission rates"],
  ["subbies", "Subcontractors (subbies)", "Subcontractor drivers – their wages sit in a separate column under Pay & commission rates"],
  ["lead_sources", "Lead sources", "Where the booking came from. Partner companies (Settings → Companies) are added to this dropdown automatically"],
  ["truck_sizes", "Truck sizes", "Options for truck capacity"],
];

export default function SettingsPage() {
  const { settings, setSettings } = useSettings();
  const { user } = useAuth();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(settings), [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...draft, daily_capacity: Number(draft.daily_capacity), driver_extra_share: Number(draft.driver_extra_share ?? 70), wrapping_driver_share: Number(draft.wrapping_driver_share ?? 50) });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage routes, colours and the dropdown lists used across bookings.">
        <Button onClick={save} disabled={!dirty || saving} data-testid="btn-save-settings" className="bg-[#FACC15] font-semibold text-[#0E1E38] hover:bg-[#EAB308]">
          <Save className="h-4 w-4" /> {dirty ? "Save changes" : "Saved"}
        </Button>
      </PageHeader>
      <Tabs defaultValue="job-types">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="job-types" data-testid="settings-tab-job-types">Job types & colours</TabsTrigger>
          <TabsTrigger value="lists" data-testid="settings-tab-lists">Lists</TabsTrigger>
          <TabsTrigger value="trucks" data-testid="settings-tab-trucks">Trucks</TabsTrigger>
          <TabsTrigger value="capacity" data-testid="settings-tab-capacity">Daily capacity</TabsTrigger>
          <TabsTrigger value="companies" data-testid="settings-tab-companies">Companies & invoicing</TabsTrigger>
          <TabsTrigger value="pay-rates" data-testid="settings-tab-pay-rates">Pay & commission rates</TabsTrigger>
          {["admin", "owner"].includes(user?.role) && <TabsTrigger value="staff" data-testid="settings-tab-staff">Staff accounts</TabsTrigger>}
          {["admin", "owner"].includes(user?.role) && <TabsTrigger value="backup" data-testid="settings-tab-backup">Backup</TabsTrigger>}
        </TabsList>
        <TabsContent value="job-types">
          <JobTypeEditor items={draft.job_types || []} onChange={(v) => setDraft({ ...draft, job_types: v })} />
        </TabsContent>
        <TabsContent value="lists">
          <div className="grid gap-4 md:grid-cols-2">
            {LISTS.map(([key, title, hint]) => (
              <ListEditor key={key} id={key} title={title} hint={hint} items={draft[key] || []} onChange={(v) => setDraft({ ...draft, [key]: v })} />
            ))}
          </div>
        </TabsContent>
        <TabsContent value="trucks"><FleetEditor draft={draft} setDraft={setDraft} /></TabsContent>
        <TabsContent value="capacity">
          <Panel title="Daily job capacity" className="max-w-lg">
            <p className="mb-3 text-sm text-slate-500">The calendar warns when a day goes over this number of jobs.</p>
            <Input type="number" min={1} max={500} value={draft.daily_capacity} onChange={(e) => setDraft({ ...draft, daily_capacity: e.target.value })} className="w-32" data-testid="field-daily-capacity" />
          </Panel>
        </TabsContent>
        {["admin", "owner"].includes(user?.role) && <TabsContent value="staff"><StaffUsers /></TabsContent>}
        {["admin", "owner"].includes(user?.role) && <TabsContent value="backup"><BackupPanel /></TabsContent>}
        <TabsContent value="companies"><CompaniesEditor draft={draft} setDraft={setDraft} /></TabsContent>
        <TabsContent value="pay-rates"><PayRatesEditor draft={draft} setDraft={setDraft} /></TabsContent>
      </Tabs>
    </div>
  );
}
